import React, { useEffect, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = '/api';

// Static map configuration constants
const FIT_BOUNDS_OPTIONS = { padding: [40, 40], maxZoom: 14 };

// Indian city fallback coordinates
const CITY_COORDS = {
  mumbai: [19.076, 72.877], delhi: [28.6139, 77.209], bangalore: [12.9716, 77.5946],
  bengaluru: [12.9716, 77.5946], hyderabad: [17.385, 78.4867], chennai: [13.0827, 80.2707],
  kolkata: [22.5726, 88.3639], pune: [18.5204, 73.8567], ahmedabad: [23.0225, 72.5714],
  surat: [21.1702, 72.8311], jaipur: [26.9124, 75.7873], lucknow: [26.8467, 80.9462],
  kanpur: [26.4499, 80.3319], nagpur: [21.1458, 79.0882], indore: [22.7196, 75.8577],
  thane: [19.2183, 72.9781], bhopal: [23.2599, 77.4126], visakhapatnam: [17.6868, 83.2185],
  patna: [25.5941, 85.1376], vadodara: [22.3072, 73.1812], noida: [28.5355, 77.3910],
  gurgaon: [28.4595, 77.0266], gurugram: [28.4595, 77.0266], kochi: [9.9312, 76.2673],
  coimbatore: [11.0168, 76.9558], agra: [27.1767, 78.0081],
};

function getFallbackCoords(city) {
  const key = (city || '').toLowerCase().trim();
  return CITY_COORDS[key] || [20.5937, 78.9629]; // India center
}

// Custom map pin icon
function createPinIcon(featured) {
  const color = featured ? '#F59E0B' : '#1D4ED8';
  const html = `<div style="
    background:${color};width:28px;height:28px;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);border:2.5px solid white;
    box-shadow:0 2px 8px rgba(0,0,0,0.25);
  "></div>`;
  return L.divIcon({ html, iconSize: [28, 28], iconAnchor: [14, 28], popupAnchor: [0, -30], className: '' });
}

function FitBounds({ pins }) {
  const map = useMap();
  useEffect(() => {
    if (pins.length === 0) return;
    if (pins.length === 1) {
      map.setView([pins[0].lat, pins[0].lng], 14);
      return;
    }
    const latLngs = pins.map(p => [p.lat, p.lng]);
    map.fitBounds(latLngs, FIT_BOUNDS_OPTIONS);
  }, [pins, map]);
  return null;
}

export default function PropertyMap({ filters }) {
  const [pins, setPins] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPins = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => { if (v && k !== 'sort') q.set(k, v); });
    }
    try {
      const res = await fetch(`${API}/properties/map-pins?${q.toString()}`);
      const data = await res.json();
      setPins(Array.isArray(data) ? data : []);
    } catch {
      setPins([]);
    }
    setLoading(false);
  }, [filters]);

  useEffect(() => { fetchPins(); }, [fetchPins]);

  // Center on first pin city or India default
  const center = pins.length > 0
    ? [pins[0].lat, pins[0].lng]
    : getFallbackCoords(filters?.city);

  return (
    <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm" style={{ height: '520px' }} data-testid="property-map">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
        </div>
      )}

      {!loading && pins.length === 0 && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-slate-50 gap-3">
          <div className="text-4xl">🗺️</div>
          <p className="text-slate-500 text-sm font-medium">No properties with map data yet</p>
          <p className="text-slate-400 text-xs">New listings will appear here automatically</p>
        </div>
      )}

      <MapContainer
        center={center}
        zoom={pins.length === 0 ? 5 : 12}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {pins.length > 0 && <FitBounds pins={pins} />}
        {pins.map(pin => (
          <Marker
            key={pin.property_id}
            position={[pin.lat, pin.lng]}
            icon={createPinIcon(pin.listing_type === 'featured' || pin.listing_type === 'premium')}
          >
            <Popup className="property-popup">
              <div style={{ minWidth: '200px' }}>
                <p style={{ fontWeight: 700, fontSize: '13px', color: '#1e293b', margin: '0 0 4px' }}>{pin.title}</p>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 4px' }}>
                  {pin.area ? `${pin.area}, ` : ''}{pin.city}
                </p>
                <p style={{ fontWeight: 700, fontSize: '14px', color: '#1D4ED8', margin: '0 0 8px' }}>
                  ₹{Number(pin.rent).toLocaleString('en-IN')}/mo
                </p>
                <a
                  href={`/property/${pin.property_id}`}
                  style={{
                    display: 'block', background: '#1D4ED8', color: 'white',
                    textAlign: 'center', padding: '6px 12px', borderRadius: '8px',
                    fontSize: '12px', fontWeight: 600, textDecoration: 'none'
                  }}
                >
                  View Details
                </a>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      <div className="absolute bottom-4 left-4 z-10 bg-white rounded-xl shadow-md px-3 py-2 text-xs text-slate-600 font-medium border border-slate-100">
        {pins.length} {pins.length === 1 ? 'property' : 'properties'} on map
      </div>
    </div>
  );
}
